import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {FormGroup, FormControl, Validators, AbstractControl} from '@angular/forms';
import { Store } from '@ngrx/store';
import { DropdownOption } from 'src/app/models/dropdown-option.model';
import {GetActivityType, GetScreenType, GetTodType} from '../../core/my-journal.selector';
import { Subject, takeUntil } from 'rxjs';
import { AddEJournalDetailsModel } from '../../model/list.model';
import { AddEJournalRequest, EditRecordsRequest } from '../../core/my-journal.actions';
import { CommonService } from 'src/app/modules/shared/services/common.service';
import { EJournalConstant } from '../../constant/e-journal.enum';

@Component({
  selector: 'app-add-daily-screen-time',
  templateUrl: './add-daily-screen-time.component.html',
  styleUrls: ['./add-daily-screen-time.component.css']
})
export class AddDailyScreenTimeComponent implements OnInit, OnDestroy {
  @Input() data: any;
  form: FormGroup = new FormGroup({
    todType: new FormControl('', [Validators.required]),
    screenType: new FormControl(null),
    screenTypeOtherValue: new FormControl(''),
    activityType: new FormControl(null),
    activityTypeOtherValue: new FormControl(''),
    hours: new FormControl(null, [Validators.max(24)]),
    minutes: new FormControl(null, [Validators.max(59)]),
    duration: new FormControl<number>(null, [Validators.required, Validators.max(1440)]),
    comments: new FormControl(''),
  }, [this.durationValidator]);
  errorMessages: any = {};
  errorMessageMap = {
    todType: {
      required: 'This field is required.',
    },
    screenType: {},
    screenTypeOtherValue: {
      required: 'This field is required.',
    },
    activityType: {},
    activityTypeOtherValue: {
      required: 'This field is required.',
    },
    hours: {},
    minutes: {},
    duration: {
      required: 'One of field is required.',
    },
  }
  todTypeList: DropdownOption[] = []
  todLoading: boolean = false;
  dataSource: any[] = [
    {
      screenType: 'Computer Screen',
      activity: 'Video Games'
    },
  ]
  colDef = [
    'screenType',
    'activity',
  ];
  mode: string;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  screenTypeList: DropdownOption[] = []
  screenTypeListLoading: boolean = false;

  activityTypeList: DropdownOption[] = []
  activityTypeListLoading: boolean = false;

  constructor(private store: Store, private commonService: CommonService) { }

  ngOnInit(): void {
    this.store.select(GetTodType).pipe(takeUntil(this.onDestroy)).subscribe(todType => {
      this.todTypeList = todType.list;
      this.todLoading = todType.loading
    })
    this.store.select(GetScreenType).pipe(takeUntil(this.onDestroy)).subscribe(data => {
      this.screenTypeList = data.list;
      this.screenTypeListLoading = data.loading
    })
    this.store.select(GetActivityType).pipe(takeUntil(this.onDestroy)).subscribe(data => {
      this.activityTypeList = data.list;
      this.activityTypeListLoading = data.loading
    })

    this.form.controls['screenType'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe((value) => {
      if (value === -1) {
        this.form.controls['screenTypeOtherValue'].setValidators([Validators.required]);
      } else {
        this.form.controls['screenTypeOtherValue'].setValue(null);
        this.form.controls['screenTypeOtherValue'].setErrors(null);
        this.form.controls['screenTypeOtherValue'].removeValidators(Validators.required);
      }
    });

    this.form.controls['activityType'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe((value) => {
      if (value === -1) {
        this.form.controls['activityTypeOtherValue'].setValidators([Validators.required]);
      } else {
        this.form.controls['activityTypeOtherValue'].setValue(null);
        this.form.controls['activityTypeOtherValue'].setErrors(null);
        this.form.controls['activityTypeOtherValue'].removeValidators(Validators.required);
      }
    });

    this.form.controls['hours'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe((value) => {
      this.form.controls['duration'].patchValue(this.getDuration());
    });

    this.form.controls['minutes'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe((value) => {
      this.form.controls['duration'].patchValue(this.getDuration());
    });

    if (this.data?.previousData) {
      this.mode = 'edit'
      let hours: any = Math.floor(this.data?.previousData?.duration / 60);
      let minutes: any = this.data?.previousData?.duration % 60;
      this.form.patchValue({
        todType: this.data?.previousData?.type,
        screenType: this.data?.previousData?.screenType?.key,
        ...(this.data?.previousData?.screenType?.key === -1 ? {screenTypeOtherValue: this.data?.previousData?.screenType?.value}: {}),
        activityType: this.data?.previousData?.activityType?.key,
        ...(this.data?.previousData?.activityType?.key === -1 ? {activityTypeOtherValue: this.data?.previousData?.activityType?.value}: {}),
        hours: hours,
        minutes: minutes,
        comments: this.data?.previousData?.comments || '',
      })
    }
    else {
      this.mode = 'add'
    }

  }

  durationValidator(formGroup: FormGroup) {
    if (formGroup.controls['duration'].errors && formGroup.controls['duration'].errors['required']) {
      formGroup.controls['hours'].setErrors({
        required: true,
      });
      formGroup.controls['minutes'].setErrors({
        required: true,
      });
    } else {
      formGroup.controls['hours'].setErrors(null);
      formGroup.controls['minutes'].setErrors(null);
    }
    return formGroup.errors;
  }

  getDuration() {
    const {hours, minutes} = this.form.getRawValue();
    if ((
      hours === null && minutes === null) ||
        ((''+hours).trim() === '' && (''+minutes).trim() === '') ||
        (hours === null && (''+minutes).trim() === '') ||
        ((''+hours).trim() === '' && minutes === null)
    ) {
      return null;
    }
    const duration = (+hours * 60) + (+minutes);
    return duration || null;
  }

  addScreenType() {
    this.errorMessages = {};
    if (this.form.invalid) {
      this.errorMessages = this.commonService.checkFormValidation(this.form, this.errorMessageMap);
      return;
    }

    const {hours, minutes, ...formValue} = this.form.getRawValue()
    if (!formValue.screenType) {
      delete formValue.screenType;
      delete formValue.screenTypeOtherValue;
    }
    if (!formValue.activityType) {
      delete formValue.activityType;
      delete formValue.activityTypeOtherValue;
    }

    if (!formValue.screenTypeOtherValue?.trim()) {
      delete formValue.screenTypeOtherValue;
    }
    if (!formValue.activityTypeOtherValue?.trim()) {
      delete formValue.activityTypeOtherValue;
    }

    if (this.mode === 'add') {
      const screenTime: any[] = [];
      screenTime.push({ ...formValue })
      const payload: AddEJournalDetailsModel['request'] = {
        date: this.data?.data?.selectedDate?.date,
        ...(this.data?.data?.memberId ? { forUserId: +this.data?.data.memberId } : {}),
        ...(this.data?.data?.groupId ? { groupId: +this.data?.data.groupId } : {}),
        foodIntake: [],
        physicalActivity: [],
        screenTime: screenTime
      }

      if (!this.data?.data?.selectedDate?.isNormalJournalingSTExists) {
        this.store.dispatch(AddEJournalRequest({
          payload: payload
        }))
        return;
      }

      this.commonService.openConfirmationDialog({
        data: {
          message: `You have a <span class="text-[rgb(0,112,224)]">Screen Time</span> total in your Behavior Tracker for this date. Do you want to delete your Behavior Tracker total and replace with the total of your e-Journal entries?`,
          submitText: 'Yes',
          classList: 'w-[40vw]',
          handleSubmit: () => {
            this.commonService.closeDialog();
            this.store.dispatch(AddEJournalRequest({
              payload: payload
            }))
          },
          cancelText: 'No',
          handleCancel: () => {
            this.commonService.closeDialog();
          }
        }
      });
    }
    else {
      this.store.dispatch(EditRecordsRequest({
        payload: {
          data: formValue,
          ...(this.data?.data?.memberId ? { forUserId: +this.data?.data.memberId } : {}),
          date: this.data?.data?.selectedDate?.date,
          params: {
            recordId: this.data.previousData.id,
            recordType: EJournalConstant.screenRecordType
          }
        }
      }))
    }
  }

  closeModel() {
    this.commonService.closeDialog();
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
